'use client'

import ReviewList from './ReviewList'
import ReviewSummaryCard from './ReviewSummaryCard'
import SectionHeading from './SectionHeading'
import { useLockAb } from '../../_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  MODOOCAR_CODE,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IModoocarDetail } from '../_features/modoocar'

interface IOwnerReviewSectionProps {
  detail: IModoocarDetail
  /** 인앱 웹뷰 여부 (전체보기 링크 분기) */
  isInApp?: boolean
  /** 인앱 '전체보기' 클릭 — 리뷰 미작성 시 리뷰 작성 유도 팝업 (Figma 9064:32674) */
  onMoreClick?: () => void
}

/** 접힘 시 노출할 후기 수 (웹: '더보기' 클릭 시 펼쳐도 동일 3건 + 앱 CTA) */
const VISIBLE_REVIEW_COUNT = 3

/**
 * 오너 후기 섹션 — 후기 요약 카드 + 후기 목록.
 * 세부모델 후기 0건이면 ModoocarDetail이 이 섹션 자체를 렌더하지 않는다.
 * 어노테이션 케이스 분기:
 * - 만족도 점수 없음: 요약 카드 미노출
 * - 후기 4건↑: 전체보기(웹: 3건 → '더보기' 시 3건 + 앱 설치 CTA / 인앱: 네이티브 모달 예정)
 */
const OwnerReviewSection = ({
  detail,
  isInApp = false,
  onMoreClick,
}: IOwnerReviewSectionProps) => {
  const {
    upperModelName,
    lowerModelName,
    ownerReviewCount,
    carOwnerReviewCount,
    ownerReview,
  } = detail
  const { averageScore, satisfactionRank, segment, reviews } = ownerReview

  const hasSatisfaction = averageScore > 0

  // 세부모델명(1줄 말줄임) / '오너 리뷰 {세부모델 리뷰 수}' 2줄 — 숫자와 같은 세부모델 기준으로 맞춘다 (WEB-6180)
  const title = (
    <>
      <span className='block truncate'>{lowerModelName}</span> 오너 리뷰
    </>
  )
  // 더보기 CTA — 상위모델 리뷰 수가 있으면 상위모델 기준 문구 (Figma 10420:6190)
  const appCtaMessage = carOwnerReviewCount
    ? `'${upperModelName}' 오너들이 남긴\n${carOwnerReviewCount.toLocaleString()}개 리뷰 더보기`
    : undefined

  const { sectionShow } = useModoocarTracking()
  const {
    data: { isLock },
  } = useLockAb()
  const sectionRef = useModoocarImpression<HTMLElement>(() =>
    sectionShow(MODOOCAR_CODE.OWNER_REVIEW, '오너 후기')
  )

  return (
    <section
      ref={sectionRef}
      id='owner-review'
      className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 py-dimension-x8 lg:scroll-mt-[112px]'
    >
      <div className='px-dimension-x5'>
        <SectionHeading
          title={title}
          highlight={ownerReviewCount.toLocaleString()}
          titleVariant='title-1-semibold'
          highlightVariant='title-1-semibold'
          titleClassName='min-w-0'
        />
      </div>

      {hasSatisfaction ? (
        <ReviewSummaryCard
          averageScore={averageScore}
          satisfactionRank={satisfactionRank}
          segment={segment}
        />
      ) : null}

      {/* 웹: '더보기' 클릭 시 펼침 + 앱 설치 CTA 오버레이 / 인앱: '전체보기'(네이티브 모달 예정) */}
      <ReviewList
        reviews={reviews}
        visibleCount={VISIBLE_REVIEW_COUNT}
        totalCount={ownerReviewCount}
        appCtaMessage={appCtaMessage}
        expandable={!isInApp}
        locked={isLock}
        isInApp={isInApp}
        onMoreClick={onMoreClick}
      />
    </section>
  )
}

export default OwnerReviewSection
